import { gql } from '@apollo/client';

export const FETCH_MITIGATION_DIVISIONS = gql`
  query GetDivisions {
    findAllDivisions {
      name
      uuid
      isDefault
      locationsPerDivision
      divisionMitigationAverage
    }
  }
`;

export const FETCH_DIVISIONS_BY_LOCATIONS = gql`
  query FindDivisionsByLocation($locations: FindByLocationInput!) {
    findDivisionsByLocation(locations: $locations) {
      uuid
      name
      isDefault
      locationsPerDivision
      divisionMitigationAverage
    }
  }
`;

export const FETCH_MITIGATION_BY_DIVISION = gql`
  query GetMitigationByDivision($division: UUIDInput!) {
    mitigationCalculates(division: $division) {
      availableExternalMitigation
      availableInternalMitigation
      combinedMitigation
      locationName
      mitigationPercentage
      netRevenueExposure
      rebuildTime
      unmitigatedRevenueExposed
      availableFgMitigation
      level
      uuid
    }
  }
`;

export const FETCH_INTERNAL_MITIGATION_MATRIX = gql`
  query GetInternalMitigationMatrix(
    $location: UUIDInput!
    $division: UUIDInput!
    $level: Int!
  ) {
    internalMitigationOptionDashboard(
      location: $location
      division: $division
      level: $level
    ) {
      division {
        __typename
        locations {
          delay
          locationName
          percentage
          key: uuid
          internalMitigationUUID
          comment
        }
        name
        children: productGroups {
          __typename
          locations {
            delay
            locationName
            percentage
            key: uuid
            internalMitigationUUID
            comment
          }
          name
          children: products {
            __typename
            locations {
              delay
              locationName
              percentage
              key: uuid
              internalMitigationUUID
              comment
            }
            name
            revenue
            key: uuid
          }
          revenue
          key: uuid
        }
        revenue
        key: uuid
      }
    }
  }
`;

export const FETCH_MITIGATORS = gql`
  query GetMitigators($location: UUIDInput!, $division: UUIDInput!) {
    selectedVendors(location: $location, division: $division) {
      assignedMitigators {
        contactName
        email
        phone
        uuid
        vendorName
      }
      unAssignedMitigators {
        contactName
        email
        phone
        uuid
        vendorName
      }
    }
  }
`;

export const CREATE_EXTERNAL_MITIGATOR = gql`
  mutation CreateExternalMitigator($data: CreateMitigatorInput!) {
    createMitigator(data: $data) {
      contactName
      email
      phone
      uuid
      vendorName
    }
  }
`;

export const UPDATE_INTERNAL_MITIGATION_DIVISION = gql`
  mutation UpdateInternalMitigationMatrix(
    $data: UpsertDivisionMitigationInput!
  ) {
    upsertDivisionInternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const UPDATE_INTERNAL_MITIGATION_PRODUCT_GROUP = gql`
  mutation UpdateInternalMitigationMatrix(
    $data: UpsertProductGroupMitigationInput!
  ) {
    upsertProductGroupInternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;
export const UPDATE_INTERNAL_MITIGATION_PRODUCT = gql`
  mutation UpdateInternalMitigationMatrix(
    $data: UpsertProductMitigationInput!
  ) {
    upsertProductInternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const FETCH_EXTERNAL_MITIGATION_MATRIX = gql`
  query GetInternalMitigationMatrix(
    $location: UUIDInput!
    $division: UUIDInput!
    $level: Int!
  ) {
    externalMitigationOptionDashboard(
      location: $location
      division: $division
      level: $level
    ) {
      division {
        __typename
        vendors {
          delay
          percentage
          key: uuid
          vendorName
          externalMitigationUUID
          comment
        }
        name
        children: productGroups {
          __typename
          vendors {
            delay
            percentage
            key: uuid
            vendorName
            externalMitigationUUID
            comment
          }
          name
          children: products {
            __typename
            vendors {
              delay
              percentage
              key: uuid
              vendorName
              externalMitigationUUID
              comment
            }
            name
            revenue
            key: uuid
          }
          revenue
          key: uuid
        }
        revenue
        key: uuid
      }
    }
  }
`;

export const CREATE_MITIGATION_MATRIX = gql`
  mutation CreateMitigationMatrix($data: CreateExternalMitigationMatrixInput!) {
    createExternalMitigationMatrix(data: $data)
  }
`;

export const UPDATE_EXTERNAL_MITIGATION_DIVISION = gql`
  mutation UpdateExternalMitigationMatrix(
    $data: UpsertDivisionMitigationInput!
  ) {
    updateDivisionExternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const UPDATE_EXTERNAL_MITIGATION_PRODUCT_GROUP = gql`
  mutation UpdateExternalMitigationMatrix(
    $data: UpsertProductGroupMitigationInput!
  ) {
    updateProductGroupExternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const UPDATE_EXTERNAL_MITIGATION_PRODUCT = gql`
  mutation UpdateExternalMitigationMatrix(
    $data: UpsertProductMitigationInput!
  ) {
    updateProductExternalMitigationOptions(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const FETCH_INTERMEDIATE_MITIGATION = gql`
  query GetIntermediateMitigationMatrix(
    $location: UUIDInput!
    $division: UUIDInput!
    $intermediateType: IntermediateTypeEnum!
  ) {
    intermediateMitigationOptionDashboard(
      location: $location
      division: $division
      intermediateType: $intermediateType
    ) {
      intermediate {
        uuid
        division {
          uuid
          name
        }
        location {
          uuid
          locationName
        }
        locationSupported {
          uuid
          locationName
        }
        locations {
          uuid
          locationName
          delay
          percentage
        }
        vendors {
          delay
          percentage
          uuid
          vendorName
        }
        name
        revenue
      }
    }
  }
`;

export const UPDATE_INTERMEDIATE_MITIGATION = gql`
  mutation UpdateIntermediateMitigation(
    $data: UpsertIntermediateMitigationInput!
  ) {
    upsertIntermediateMitigation(data: $data) {
      delay
      percentage
      uuid
    }
  }
`;

export const UPDATE_INTERNAL_MITIGATION_COMMENT = gql`
  mutation UpdateInternalMitigationComment(
    $input: UpdateInternalMitigationCommentInput!
  ) {
    updateInternalMitigationComment(input: $input) {
      uuid
    }
  }
`;

export const UPDATE_EXTERNAL_MITIGATION_COMMENT = gql`
  mutation UpdateExternalMitigationComment(
    $input: UpdateExternalMitigationCommentInput!
  ) {
    updateExternalMitigationComment(input: $input) {
      uuid
    }
  }
`;
